import { useEffect, useState } from 'react';
import { Localized } from '@fluent/react';
import { LoadingButton } from '@mui/lab';
import { Alert, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useEditor } from '@tiptap/react';
import { CancelButton } from '../../../../app/components/CancelButton';
import { RichTextContainer, tiptapExtensions } from '../../../../app/RichTextEditor';

interface AiGenerationQuestionDialogProps {
    titleId: string;
    question: string;
    generating: boolean;
    error: string | null;
    onDismissError: () => void;
    onClose: () => void;
    onGenerate: (question: string) => void;
}

export const AiGenerationQuestionDialog = ({
    titleId, question, generating, error, onDismissError, onClose, onGenerate,
}: AiGenerationQuestionDialogProps) => {
    const [questionText, setQuestionText] = useState(question);
    const editor = useEditor({
        extensions: tiptapExtensions,
        content: {
            type: 'doc',
            content: question.split('\n').map(line => ({
                type: 'paragraph',
                content: line ? [{ type: 'text', text: line }] : [],
            })),
        },
        onUpdate: ({ editor }) => setQuestionText(editor.getText({ blockSeparator: '\n' })),
    });

    useEffect(() => {
        editor?.setEditable(!generating);
    }, [editor, generating]);

    return (
        <Dialog open onClose={generating ? undefined : onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ bgcolor: 'grey.200' }}>
                <Localized id={titleId}>_Generate with AI</Localized>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
                <RichTextContainer
                    label={<Localized id="test-ai-question">_Question for AI</Localized>}
                    editor={editor}
                />
                {error && (
                    <Alert sx={{ mt: 1 }} onClose={onDismissError} severity="error">
                        <Localized id={error}><span>_Server error</span></Localized>
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <LoadingButton
                    loading={generating}
                    disabled={!editor || !questionText.trim()}
                    onClick={() => {
                        if (editor) onGenerate(editor.getText({ blockSeparator: '\n' }));
                    }}
                    size="small"
                    color="success"
                    variant="contained"
                >
                    <Localized id="generate">_Generate</Localized>
                </LoadingButton>
                <CancelButton loading={generating} onClick={onClose} />
            </DialogActions>
        </Dialog>
    );
};
